import React, { useEffect } from 'react'
import { colors, Row, Paragraph } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { VisuallyHidden } from '@reach/visually-hidden'
import { convertMsToTime, getNowTimestamp } from '../helpers'
import { second, minute, day } from '../../config'
import useCountdownTimer from './useCountdownTimer'

// Column component that mimics Row but with vertical layout
const Column = ({ children, alignX = 'center', ...props }) => {
  const childArray = React.Children.toArray(children)

  const alignItems =
    {
      start: 'flex-start',
      center: 'center',
      end: 'flex-end',
    }[alignX] || 'flex-start'

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems,
        gap: `calc(var(--gutters-y) *2))`,
      }}
      {...props}
    >
      {childArray}
    </div>
  )
}

const getDigits = (num) => {
  if (!num || num < 0 || num > 999) {
    return [0, 0, 0]
  }

  const digitStr = num.toString().padStart(3, '0')
  const digits = digitStr.split('').map(Number)
  return digits
}

const digitBoxCss = (color) => css`
  background-color: ${color === 'blue' ? colors.blue02 : colors.gray02};
  border-radius: 4px;
  padding: var(--spacer-pico) var(--spacer-nano);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  min-height: 1.5rem;
`

const Digit = ({ number, color, size = 'lg' }) => {
  return (
    <div css={digitBoxCss(color)}>
      <Paragraph as="strong" size={size} centered>
        {number}
      </Paragraph>
    </div>
  )
}

const DigitGroup = ({ num, color, size }) => {
  const [digit1, digit2, digit3] = getDigits(num)

  return (
    <Row gutters="pico">
      {digit1 !== 0 && <Digit number={digit1} color={color} size={size} />}
      <Digit number={digit2} color={color} size={size} />
      <Digit number={digit3} color={color} size={size} />
    </Row>
  )
}

const calcDigitLabelSize = (size) =>
  ({
    xs: 'xxs',
    sm: 'xs',
    md: 'sm',
    lg: 'md',
    xl: 'lg',
  })[size] || 'md'

const CountdownPeriod = ({ num, color, size, label }) => (
  <Column alignX="center" gutters="nano">
    <DigitGroup color={color} num={num} size={size} />
    <Paragraph as="span" centered size={calcDigitLabelSize(size)}>
      {label}
    </Paragraph>
  </Column>
)

// Custom hook to handle page reload when countdown finishes
const useCountdownReload = (timeRemaining, noReload) => {
  useEffect(() => {
    if (timeRemaining === 0 && !noReload) {
      window.location.reload()
    }
  }, [timeRemaining, noReload])
}

// Custom hook to manage countdown display state
const useCountdownDisplay = (startTimestamp) => {
  const hasDays = startTimestamp - getNowTimestamp() > day
  const timeToWait = !hasDays ? second : minute
  const timeRemaining = useCountdownTimer(startTimestamp, timeToWait)
  const timeValues = convertMsToTime(timeRemaining)

  return {
    hasDays,
    timeRemaining,
    ...timeValues,
  }
}

const VisuallyHiddenCountdown = ({ startTimestamp, timesToShow }) => {
  const [t] = useTranslation()
  const { days, hours, minutes, seconds, hasDays } = useCountdownDisplay(startTimestamp)
  return (
    <VisuallyHidden>
      {hasDays && (
        <>
          {days} {t('homepage.countdown.days', 'Days')}{' '}
        </>
      )}
      {timesToShow.hours && (
        <>
          {hours} {t('homepage.countdown.hours', 'Hours')}{' '}
        </>
      )}
      {timesToShow.minutes && (
        <>
          {minutes} {t('homepage.countdown.minutes', 'Minutes')}{' '}
        </>
      )}
      {!hasDays && (
        <>
          {seconds} {t('homepage.countdown.secondsRemaining', 'Seconds')}{' '}
        </>
      )}
    </VisuallyHidden>
  )
}

// Default wrapper that passes through children without modification
const PassThrough = ({ children }) => <>{children}</>

export default function CountDown({
  startTimestamp,
  timesToShow = { days: true, hours: true, minutes: true, seconds: false },
  color,
  noReload,
  children,
  Wrapper = PassThrough,
  size = 'lg',
}) {
  const [t] = useTranslation()
  const { hasDays, timeRemaining, days, hours, minutes, seconds } = useCountdownDisplay(startTimestamp)
  useCountdownReload(timeRemaining, noReload)

  const isDone = timeRemaining === 0
  return (
    <>
      {!isDone && (
        <Wrapper startTimestamp={startTimestamp}>
          {/* there was not a reasonable way to make the countdown accessible. So we have a separate feature for screen readers. */}
          <VisuallyHiddenCountdown startTimestamp={startTimestamp} timesToShow={timesToShow} />
          <Row aria-hidden alignY="middle">
            {hasDays && (
              <CountdownPeriod
                noShrinkX
                num={days}
                color={color}
                size={size}
                label={t('homepage.countdown.days', 'Days')}
              />
            )}
            {timesToShow.hours && (
              <CountdownPeriod
                noShrinkX
                num={hours}
                color={color}
                size={size}
                label={t('homepage.countdown.hours', 'Hours')}
              />
            )}
            {timesToShow.minutes && (
              <CountdownPeriod
                noShrinkX
                num={minutes}
                color={color}
                size={size}
                label={t('homepage.countdown.minutes', 'Minutes')}
              />
            )}
            {!hasDays && (
              <CountdownPeriod
                noShrinkX
                num={seconds}
                color={color}
                size={size}
                label={t('homepage.countdown.secondsRemaining', 'Seconds')}
              />
            )}
          </Row>
        </Wrapper>
      )}

      {isDone && children}
    </>
  )
}
